// src/onboarding/onboardingStorage.js
//
// Two markers, both best-effort (every access in try/catch — storage can be blocked or full):
//  • the PROMPT marker (localStorage): which workspaces were already prompted in THIS login.
//    ONE entry `{loginKey, tenants}` — a new login (new token) starts a new list, so entries never
//    pile up. ⛔ The raw token is never stored: `loginKey` is a non-cryptographic hash of it.
//  • the PENDING-START marker (sessionStorage): a 202 waiting for its run to appear (decision 15),
//    per tab, so a reload inside the PENDING_START_TIMEOUT_MS window (onboardingModel) does not lose it.
const PROMPT_KEY = 'onboarding_prompted';
const PENDING_KEY = 'onboarding_pending_start';

/** djb2 over the token → short hex. An identifier, not a secret. */
export function tokenKey(token) {
  if (!token) return '';
  let h = 5381;
  for (let i = 0; i < token.length; i += 1) h = ((h * 33) ^ token.charCodeAt(i)) >>> 0; // eslint-disable-line no-bitwise
  return h.toString(16);
}

function readJson(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
  } catch { /* best effort */ }
}

export function hasPrompted(tenantId, loginKey) {
  const entry = readJson(localStorage, PROMPT_KEY);
  return !!entry && entry.loginKey === loginKey && Array.isArray(entry.tenants) && entry.tenants.includes(tenantId);
}

export function markPrompted(tenantId, loginKey) {
  const entry = readJson(localStorage, PROMPT_KEY);
  const tenants = entry && entry.loginKey === loginKey && Array.isArray(entry.tenants) ? entry.tenants : [];
  if (tenants.includes(tenantId)) return;
  writeJson(localStorage, PROMPT_KEY, { loginKey, tenants: [...tenants, tenantId] });
}

/**
 * v192: the marker remembers the BACKEND's own values from before the POST (`prevAttemptRunId` = the
 * newest run's id, `prevErrorAt` = the current start error's `at`), so the poller decides by a CHANGE
 * of either — never by comparing against the browser's clock. A v191 marker (`prevRunId`) still reads.
 * @returns {{tenantId: string, prevAttemptRunId: string|null, prevErrorAt: string|null, at: number}|null}
 *   for THIS tenant only
 */
export function readPendingStart(tenantId) {
  const entry = readJson(sessionStorage, PENDING_KEY);
  if (!entry || entry.tenantId !== tenantId || typeof entry.at !== 'number') return null;
  return {
    tenantId,
    prevAttemptRunId: entry.prevAttemptRunId ?? entry.prevRunId ?? null,
    prevErrorAt: entry.prevErrorAt ?? null,
    at: entry.at,
  };
}

export function writePendingStart(tenantId, { prevAttemptRunId = null, prevErrorAt = null } = {}, at = Date.now()) {
  writeJson(sessionStorage, PENDING_KEY, { tenantId, prevAttemptRunId, prevErrorAt, at });
}

export function clearPendingStart() {
  writeJson(sessionStorage, PENDING_KEY, null);
}
